"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
    Mail, Phone, MapPin, Send, Loader2, Facebook,
    Youtube, Linkedin, CheckCircle2, ArrowRight, Github,
    MessageSquare, ExternalLink, Music, Sparkles
} from "lucide-react";
import AntiSpamCaptcha from "@/components/AntiSpamCaptcha";

export default function ContactPage() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [profile, setProfile] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");
    const [captchaValid, setCaptchaValid] = useState(false);

    useEffect(() => {
        fetch("/api/profile").then(r => r.json()).then(setProfile).catch(() => { });
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true); setError(""); setSuccess(false);
        try {
            const res = await fetch("/api/contact", {
                method: "POST",
                body: JSON.stringify(form),
                headers: { "Content-Type": "application/json" }
            });
            if (res.ok) {
                setSuccess(true);
                setForm({ name: "", email: "", subject: "", message: "" });
            } else {
                const d = await res.json();
                setError(d.error || "Une erreur est survenue.");
            }
        } catch { setError("Erreur réseau. Veuillez réessayer."); }
        finally { setLoading(false); }
    };

    const socials = [
        { icon: <Facebook size={20} />, href: profile?.facebook, label: "Facebook" },
        { icon: <Youtube size={20} />, href: profile?.youtube, label: "Youtube" },
        { icon: <Linkedin size={20} />, href: profile?.linkedin, label: "Linkedin" },
        { icon: <Github size={20} />, href: profile?.github, label: "Github" },
        { icon: <MessageSquare size={20} />, href: profile?.whatsapp ? `https://wa.me/${profile.whatsapp.replace(/[^0-9]/g, "")}` : null, label: "WhatsApp" },
        { icon: <ExternalLink size={20} />, href: profile?.freelance, label: "Freelance" },
        { icon: <Music size={20} />, href: profile?.tiktok, label: "TikTok" },
    ].filter(s => s.href);

    return (
        <main style={{ paddingTop: "10rem", paddingBottom: "8rem", minHeight: "100vh" }}>
            <div className="container" style={{ maxWidth: "80rem" }}>
                {/* Header */}
                <header style={{ marginBottom: "6rem" }}>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <div className="badge badge-primary" style={{ marginBottom: "1.5rem" }}>
                            Collaboration & Partenariat
                        </div>
                    </motion.div>
                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: "2rem" }}>
                        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.1 }} style={{ maxWidth: "45rem" }}>
                            <h1 style={{ fontSize: "clamp(2.5rem, 6vw, 4rem)", marginBottom: "1.5rem", lineHeight: 1.1 }}>
                                Donnons Vie à vos <span className="text-gradient">Idées.</span>
                            </h1>
                            <p style={{ color: "var(--muted-foreground)", fontSize: "1.15rem", lineHeight: 1.8 }}>
                                Que ce soit pour un projet IoT complexe, une architecture logicielle ou une simple question, je suis à votre écoute pour transformer vos visions en solutions techniques concrètes.
                            </p>
                        </motion.div>
                    </div>
                </header>

                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "3rem" }} className="contact-grid">
                    {/* Info Side */}
                    <motion.aside initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
                        style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
                        <div className="card contact-info-card" style={{ background: "var(--primary)", color: "white", position: "relative", overflow: "hidden", border: "none" }}>
                            <div style={{ position: "absolute", top: "-2rem", right: "-2rem", width: "12rem", height: "12rem", background: "rgba(255,255,255,0.1)", borderRadius: "50%", filter: "blur(40px)" }} />
                            <div style={{ position: "absolute", bottom: "-4rem", left: "-4rem", width: "16rem", height: "16rem", background: "rgba(0,0,0,0.1)", borderRadius: "50%", filter: "blur(60px)" }} />

                            <h2 style={{ fontSize: "2rem", marginBottom: "3rem", position: "relative", fontWeight: 900 }}>Coordonnées.</h2>

                            <div style={{ display: "flex", flexDirection: "column", gap: "2.5rem", position: "relative" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                                    <div style={{ width: 56, height: 56, background: "rgba(255,255,255,0.15)", borderRadius: "var(--radius-lg)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)", flexShrink: 0 }}>
                                        <Mail size={24} />
                                    </div>
                                    <div style={{ overflow: "hidden" }}>
                                        <p style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.8, marginBottom: "0.25rem", fontWeight: 800 }}>Email professionnel</p>
                                        <p style={{ fontSize: "1.1rem", fontWeight: 700, wordBreak: "break-all" }}>{profile?.email || "contact@metalab.africa"}</p>
                                    </div>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                                    <div style={{ width: 56, height: 56, background: "rgba(255,255,255,0.15)", borderRadius: "var(--radius-lg)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)", flexShrink: 0 }}>
                                        <Phone size={24} />
                                    </div>
                                    <div>
                                        <p style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.8, marginBottom: "0.25rem", fontWeight: 800 }}>Téléphone / WhatsApp</p>
                                        <p style={{ fontSize: "1.1rem", fontWeight: 700 }}>{profile?.phone || "+229 XX XX XX XX"}</p>
                                    </div>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                                    <div style={{ width: 56, height: 56, background: "rgba(255,255,255,0.15)", borderRadius: "var(--radius-lg)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)", flexShrink: 0 }}>
                                        <MapPin size={24} />
                                    </div>
                                    <div>
                                        <p style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.8, marginBottom: "0.25rem", fontWeight: 800 }}>Localisation</p>
                                        <p style={{ fontSize: "1.1rem", fontWeight: 700 }}>{profile?.address || "Bénin, Afrique de l'Ouest"}</p>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginTop: "5rem", borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: "3rem" }}>
                                <p style={{ fontSize: "0.8rem", textTransform: "uppercase", fontWeight: 900, marginBottom: "1.5rem", opacity: 0.9, letterSpacing: "0.05em" }}>Présence Digitale</p>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
                                    {socials.map((s, i) => (
                                        <a key={i} href={s.href!} target="_blank" rel="noopener noreferrer"
                                            style={{ width: 48, height: 48, background: "rgba(255,255,255,0.15)", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)", transition: "all 0.3s" }}
                                            className="social-link" title={s.label}>
                                            {s.icon}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="card" style={{ padding: "1.5rem 2rem", display: "flex", alignItems: "center", gap: "1.5rem", background: "var(--muted)" }}>
                            <div style={{ width: 48, height: 48, background: "var(--primary-light)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <Sparkles className="text-primary" size={22} />
                            </div>
                            <p style={{ fontSize: "0.95rem", color: "var(--foreground)", fontWeight: 700, lineHeight: 1.5 }}>
                                Je m'engage à répondre à toutes les sollicitations professionnelles sous <span className="text-primary">48 heures</span>.
                            </p>
                        </div>
                    </motion.aside>

                    {/* Form Side */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
                        className="card contact-form-card">
                        {success ? (
                            <div style={{ textAlign: "center", padding: "4rem 0" }}>
                                <div style={{ width: 80, height: 80, background: "var(--primary-light)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 2rem" }}>
                                    <CheckCircle2 size={40} className="text-primary" />
                                </div>
                                <h2 style={{ fontSize: "2rem", marginBottom: "1rem", fontWeight: 900 }}>Transmission Réussie.</h2>
                                <p style={{ color: "var(--muted-foreground)", marginBottom: "3rem", fontSize: "1.1rem" }}>
                                    Votre message a bien été reçu. Je reviendrai vers vous très prochainement.
                                </p>
                                <button onClick={() => setSuccess(false)} className="btn btn-primary">
                                    Envoyer un autre message <ArrowRight size={20} style={{ marginLeft: "0.75rem" }} />
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
                                <h2 style={{ fontSize: "1.8rem", marginBottom: "0.5rem", fontWeight: 900 }}>Formulaire de Contact</h2>

                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "2rem" }}>
                                    <div className="form-group">
                                        <label className="form-label" style={{ marginBottom: "0.75rem", display: "block", fontSize: "0.8rem", fontWeight: 900, textTransform: "uppercase", color: "var(--muted-foreground)", letterSpacing: "0.05em" }}>Nom complet</label>
                                        <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="form-input" style={{ width: "100%", padding: "1.1rem", background: "var(--background)", border: "1.5px solid var(--border)", borderRadius: "1rem" }} placeholder="Jean Dupont" />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label" style={{ marginBottom: "0.75rem", display: "block", fontSize: "0.8rem", fontWeight: 900, textTransform: "uppercase", color: "var(--muted-foreground)", letterSpacing: "0.05em" }}>Adresse Email</label>
                                        <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="form-input" style={{ width: "100%", padding: "1.1rem", background: "var(--background)", border: "1.5px solid var(--border)", borderRadius: "1rem" }} placeholder="jean.dupont@entreprise.com" />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label" style={{ marginBottom: "0.75rem", display: "block", fontSize: "0.8rem", fontWeight: 900, textTransform: "uppercase", color: "var(--muted-foreground)", letterSpacing: "0.05em" }}>Sujet de votre demande</label>
                                    <input type="text" required value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="form-input" style={{ width: "100%", padding: "1.1rem", background: "var(--background)", border: "1.5px solid var(--border)", borderRadius: "1rem" }} placeholder="Collaboration / Expertise IoT / Devis..." />
                                </div>

                                <div className="form-group">
                                    <label className="form-label" style={{ marginBottom: "0.75rem", display: "block", fontSize: "0.8rem", fontWeight: 900, textTransform: "uppercase", color: "var(--muted-foreground)", letterSpacing: "0.05em" }}>Message détaillé</label>
                                    <textarea required rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="form-input" style={{ width: "100%", padding: "1.1rem", background: "var(--background)", border: "1.5px solid var(--border)", borderRadius: "1rem", resize: "none" }} placeholder="Décrivez votre projet ou votre besoin technique ici..." />
                                </div>

                                <AntiSpamCaptcha onVerify={setCaptchaValid} />

                                {error && (
                                    <div style={{ padding: "1.25rem", background: "rgba(239,68,68,0.08)", borderRadius: "var(--radius-md)", color: "#ef4444", fontSize: "0.95rem", fontWeight: 800, border: "1px solid rgba(239,68,68,0.2)" }}>
                                        {error}
                                    </div>
                                )}

                                <button type="submit" disabled={loading || !captchaValid} className="btn btn-primary" style={{ width: "100%", padding: "1.25rem", justifyContent: "center", fontSize: "1.1rem", fontWeight: 900 }}>
                                    {loading ? (
                                        <><Loader2 size={24} className="animate-spin" /> ENVOI EN COURS...</>
                                    ) : (
                                        <>TRANSMETTRE LE MESSAGE <Send size={22} style={{ marginLeft: "0.75rem" }} /></>
                                    )}
                                </button>
                            </form>
                        )}
                    </motion.div>
                </div>
            </div>

            <style>{`
                .contact-info-card { padding: 4rem; }
                .contact-form-card { padding: 4rem; }
                @media(min-width: 1024px) {
                    .contact-grid { grid-template-columns: 4fr 7fr !important; }
                }
                @media(max-width: 768px) {
                    .contact-info-card, .contact-form-card { padding: 2.5rem 1.5rem !important; }
                }
                .social-link:hover {
                    background: white !important;
                    color: var(--primary) !important;
                    transform: translateY(-4px);
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                }
            `}</style>
        </main>
    );
}
